/* screens/reports/ReportsList.jsx — единый список отчётов (S8, §2.3). НОВЫЙ экран —
   единая точка всех AI-отчётов (фикс боли №3). Перекрывает плейсхолдер window.ReportsScreen.
   Фильтры по типу (?type) и сущности (?entity) — в URL. Таблица на md+ ↔ карточки на xs/sm
   (cv-table row-as-card). Состояния: loading / empty_all / empty_filtered / 403.
   ХЕНДОФФ → pages/reports/ReportsList.jsx (новый маршрут /reports). */

import React from 'react';
import { PageHeader } from '../../components/patterns/PageHeader.jsx';
import { EmptyState, QueryBoundary } from '../../components/patterns/States.jsx';
import { Pill } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { FilterChip } from '../../components/ui/Chip.jsx';
import { SearchInput } from '../../components/ui/Field.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { useToast } from '../../app/providers/ToastProvider.jsx';
import { hooks } from '../../hooks/index.jsx';
import { reportsReal } from '../../lib/adapters/reports.js';
import { downloadReportDocx } from '../../lib/api/reports.js';
import { fmt } from '../../lib/format.js';
import { RR } from '../../lib/router.jsx';

  const { useMemo, useState } = React;
  const { useNavigate, useSearchParams } = RR;
  const { useFakeQuery, useDebouncedSearchParam } = hooks;

  // type на бэке — короткий slug ('foodcost'|'abc'|'inventory');
  // фильтры и TypePill ключатся по нему (deep-link ?type=foodcost).
  const TYPE_META = {
    foodcost: { tone: 'accent', icon: 'percent', label: 'Оптимизация food cost' },
    abc: { tone: 'info', icon: 'analytics', label: 'ABC-анализ' },
    inventory: { tone: 'neutral', icon: 'box', label: 'Анализ инвентаризации' },
  };
  const TYPE_FILTERS = [
    { key: 'all', label: 'Все', icon: 'reports' },
    { key: 'foodcost', label: 'Food cost', icon: 'percent' },
    { key: 'abc', label: 'ABC', icon: 'analytics' },
    { key: 'inventory', label: 'Инвентаризация', icon: 'box' },
  ];

  function TypePill({ type }) {
    const m = TYPE_META[type] || { tone: 'neutral', icon: 'reports', label: type };
    return <Pill tone={m.tone} icon={m.icon} size="sm">{m.label}</Pill>;
  }

  function ReportsScreen() {
    const navigate = useNavigate();
    const toast = useToast();
    const [params, setParams] = useSearchParams();
    // id отчёта, который сейчас скачивается (спиннер на его кнопке);
    // строка кликабельна целиком → у кнопки stopPropagation.
    const [downloadingId, setDownloadingId] = useState(null);
    const downloadDocx = async (r) => {
      if (downloadingId) return;
      setDownloadingId(r.id);
      try {
        await downloadReportDocx(r.id, (r.title || 'report-' + r.id) + '.docx');
      } catch {
        toast.show('Не удалось скачать файл. Попробуйте ещё раз.', 'danger');
      } finally { setDownloadingId(null); }
    };
    const typeKey = params.get('type') || 'all';
    const [entity, setEntity] = useDebouncedSearchParam('entity', 300);

    const setType = (key) => setParams((prev) => {
      const sp = new URLSearchParams(prev);
      if (key && key !== 'all') sp.set('type', key); else sp.delete('type');
      return sp;
    });
    const resetFilters = () => setParams((prev) => {
      const sp = new URLSearchParams(prev);
      sp.delete('type'); sp.delete('entity');
      return sp;
    });

    const entityUrl = params.get('entity') || '';
    const hasFilters = (typeKey && typeKey !== 'all') || !!entityUrl;

    // type — серверный фильтр (короткий slug); entity — текстовый поиск client-side (бэк не умеет).
    const q = useFakeQuery('reports-list-' + typeKey + '-' + entityUrl,
      () => reportsReal.listReports({ type: typeKey !== 'all' ? typeKey : undefined, entity: entityUrl }), [typeKey, entityUrl]);
    const rows = q.data || [];

    const toolbar = (
      <div className="reports-toolbar">
        <div className="reports-toolbar__filters">
          {TYPE_FILTERS.map((t) => (
            <FilterChip key={t.key} icon={t.icon} active={typeKey === t.key} onToggle={() => setType(t.key)}>{t.label}</FilterChip>
          ))}
        </div>
        <SearchInput value={entity} onChange={setEntity} placeholder="Поиск по названию…" className="reports-toolbar__search" />
      </div>
    );

    return (
      <div className="section-page">
        <PageHeader subtitle="Рабочее пространство" title="Отчёты" />
        <div className="section-body stack stack-16">
          {toolbar}
          <QueryBoundary loading={q.loading} error={q.error} onRetry={q.reload}
            skeleton={<div className="reports-skel">{[0, 1, 2, 3].map((i) => <div className="report-skel-row" key={i}><span className="skeleton" style={{ width: '46%', height: 16 }} /><span className="skeleton" style={{ width: 120, height: 22, borderRadius: 999 }} /><span className="skeleton" style={{ width: 80, height: 14 }} /></div>)}</div>}>
            {rows.length === 0 ? (
              hasFilters
                ? <EmptyState serif={false} icon="search" title="Ничего не найдено"
                    body="По выбранным фильтрам отчётов нет. Сбросьте фильтры, чтобы увидеть все."
                    action={<Button variant="secondary" iconLeft="close" onClick={resetFilters}>Сбросить фильтры</Button>} />
                : <EmptyState icon="reports" title="Пока нет отчётов"
                    body="AI-отчёты (оптимизация food cost, ABC-анализ, разбор инвентаризации) появятся здесь после анализа в Ассистенте или Кухне." />
            ) : (
              <div className="cv-table-wrap reports-list">
                <table className="cv-table reports-table">
                  <thead><tr><th>Название</th><th>Тип</th><th>Дата</th><th>Автор</th><th aria-label="Действие" /></tr></thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id} className="reports-table__row" role="button" tabIndex={0}
                        onClick={() => navigate('/reports/' + r.id)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') { e.preventDefault(); navigate('/reports/' + r.id); } }}>
                        <td data-label="Название">
                          <span className="cv-strong">{r.title}</span>
                          {r.summary && <span className="reports-table__sum t-caption text-mute clamp-1">{r.summary}</span>}
                        </td>
                        <td data-label="Тип"><TypePill type={r.type} /></td>
                        <td data-label="Дата" className="num">{fmt.date(r.created_at)}</td>
                        <td data-label="Автор" className="text-dim reports-table__author">{r.created_by_email}</td>
                        <td className="reports-table__go">
                          <button type="button" className="icon-btn-mini" disabled={downloadingId === r.id}
                            aria-label="Скачать .docx" title="Скачать .docx"
                            onClick={(e) => { e.stopPropagation(); downloadDocx(r); }}
                            onKeyDown={(e) => e.stopPropagation()}>
                            <Icon name={downloadingId === r.id ? 'spinner' : 'download'} size={16} className={downloadingId === r.id ? 'icon--spin' : undefined} />
                          </button>
                          <Icon name="chevron-right" size={16} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </QueryBoundary>
        </div>
      </div>
    );
  }

export { ReportsScreen };
